import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useSupportMessages } from '../../../hooks/TextMessage';
import { useTabLazyLoad } from '../../../hooks/useTabLazyLoad';
import BaseHtmlTable from '../../../table/BaseHtmlTable';
import { formatDate } from '../../../format/DateTime';
import SupportAssignmentInfo from './SupportAssignmentInfo';
import { InmateSupportAssignedToType } from '@reliance/types/enums/InmateSupportAssignedToType';
import { TextMessageSentBy } from '@reliance/types/enums/TextMessageSentBy';

type SupportMessagesProps = {
  inmateId?: number;
  onRefund?: (activity: { activityId: number; notifyInmate: boolean; description: string }) => Promise<any>;
  tabSelected?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  refreshTrigger?: number;
  onReassign?: (assignedToType: InmateSupportAssignedToType) => Promise<void>;
};

const SupportMessagesTable: React.FC<SupportMessagesProps> = (props) => {
  const { inmateId, onRefund, tabSelected, onLoadingChange, refreshTrigger, onReassign } = props;

  const [refundingActivityId, setRefundingActivityId] = useState<number | null>(null);

  // Pass null to prevent auto-execution until the tab is selected
  const supportMessages = useSupportMessages(null, true);

  // Handle lazy loading and refresh logic
  useTabLazyLoad({
    key: inmateId,
    tabSelected: tabSelected,
    refreshTrigger: refreshTrigger,
    execute: supportMessages.execute
  });

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(supportMessages.loading);
    }
  }, [supportMessages.loading, onLoadingChange]);

  const refundable = (rowData: any): boolean => {
    return (
      rowData.activity &&
      rowData.activity.length !== 0 &&
      !rowData.activity[0].reversed &&
      parseFloat(rowData.activity[0].amount) !== 0.00 &&
      onRefund != null
    );
  };

  const handleRefund = (rowData: any): void => {
    if (onRefund && rowData.activity && rowData.activity.length > 0) {
      const activityId = rowData.activity[0].id;
      setRefundingActivityId(activityId);

      const refundActivity = {
        activityId: activityId,
        notifyInmate: false,
        description: 'Support message refund',
      };

      onRefund(refundActivity)
        .then(() => {
          setRefundingActivityId(null);
          // Refresh data from the server
          if (supportMessages.execute && inmateId) {
            supportMessages.execute(inmateId);
          }
        })
        .catch(() => {
          setRefundingActivityId(null);
        });
    }
  };

  const isAIPreview = (sentBy: string): boolean => {
    return sentBy === TextMessageSentBy.AI_PREVIEW;
  };

  const getReadState = (rowData: any): string => {
    if (isAIPreview(rowData.sentBy)) {
      return 'N/A';
    }
    if (rowData.direction === 'Outbound') {
      return rowData.status !== 'Processed' ? 'Unread' : 'Read';
    } else {
      return rowData.status !== 'Sent' ? 'Unread' : 'Read';
    }
  };

  const renderSentBy = (sentBy: string): React.ReactNode => {
    const isStaff = sentBy === TextMessageSentBy.STAFF;
    const isAI = sentBy === TextMessageSentBy.AI;
    const isAIPreviewRow = isAIPreview(sentBy);

    let icon: React.ReactNode = null;
    if (isStaff) {
      icon = <i className="fas fa-id-badge text-warning me-1"></i>;
    } else if (isAI) {
      icon = <i className="fas fa-robot text-info me-1"></i>;
    } else if (isAIPreviewRow) {
      icon = <i className="fas fa-robot text-secondary me-1"></i>;
    }

    return (
      <span>
        {icon}
        {sentBy}
      </span>
    );
  };

  const columns = [
    { key: 'createdDate', name: 'Date' },
    { key: 'sentBy', name: 'Sent By' },
    { key: 'readState', name: 'Read State' },
    { key: 'message', name: 'Message', style: { maxWidth: '500px' } },
    { key: 'options', name: 'Options' },
  ];

  const data = supportMessages.result?.data || [];
  const rows = data.map((rowData: any) => {
    const activityId = rowData.activity && rowData.activity.length > 0 ? rowData.activity[0].id : null;
    const isRefunding = activityId !== null && refundingActivityId === activityId;

    return {
      id: rowData.id,
      sentByValue: rowData.sentBy, // Store original value for row className check
      createdDate: formatDate(rowData.createdDate),
      sentBy: renderSentBy(rowData.sentBy),
      readState: getReadState(rowData),
      message: rowData.message,
      options: refundable(rowData) ? (
        <Button
          variant="primary"
          disabled={isRefunding}
          onClick={() => handleRefund(rowData)}>
          {isRefunding ? 'Refunding...' : 'Refund'}
        </Button>
      ) : null,
    };
  });

  if (!tabSelected) {
    return null;
  }

  if (supportMessages.loading) {
    return (
      <div className="text-center mb-3 mt-3">
        <i className="fas fa-spin fa-spinner"></i> Loading support messages...
      </div>
    );
  }

  if (supportMessages.error) {
    return (
      <div className="alert alert-danger mb-3 mt-3">
        Error loading support messages
      </div>
    );
  }

  if (!supportMessages.result || !supportMessages.result.data || data.length === 0) {
    return null;
  }

  const inmateSupport = supportMessages.result?.supportingData?.inmateSupport;

  return (
    <div className="pb-3 mt-3">
      <SupportAssignmentInfo
        inmateSupport={inmateSupport}
        inmateId={inmateId}
        onReassign={onReassign}
      />
      <BaseHtmlTable
        columns={columns}
        rows={rows}
        perPage={1000}
        getRowClassName={(row: any) => isAIPreview(row.sentByValue) ? 'fst-italic' : ''}
      />
    </div>
  );
};

export default SupportMessagesTable;
